import React from 'react'
import classnames from 'classnames'
import { createPopper, Instance, Placement } from '@popperjs/core'

import styles from './styles.module.scss'

const POPPER_OPTIONS = {
  placement: 'top' as Placement,
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, 16],
      },
    },
    {
      name: 'flip',
      options: {
        fallbackPlacements: ['top', 'bottom'],
        allowedAutoPlacements: ['top', 'bottom'],
        rootBoundary: 'document',
      },
    },
  ],
}

export interface Props extends React.PropsWithChildren {
  active: boolean
  toggleActive: () => void
  triggerElement: HTMLElement
}

const Popover: React.FC<Props> = (props) => {
  const { active, children, toggleActive, triggerElement } = props
  const popoverRef = React.useRef<HTMLDivElement>()
  const popperInstance = React.useRef<Instance>()
  const clickOutside = React.useCallback(
    (event: MouseEvent): void => {
      if (!event.target || !active || !popoverRef.current) {
        return
      }

      const target = event.target as HTMLElement
      const clickWasOutsidePopover = !target.closest('.like-popover')
      const clickWasOutsidePopoverBtn = !target.closest(`#${triggerElement.id}`)

      if (clickWasOutsidePopover && clickWasOutsidePopoverBtn) {
        toggleActive()
      }
    },
    [active, toggleActive, triggerElement],
  )

  React.useEffect(() => {
    document.addEventListener('mouseup', clickOutside)

    return () => {
      document.removeEventListener('mouseup', clickOutside)
    }
  }, [clickOutside])

  React.useEffect(() => {
    const destroyPopover = (): void => {
      if (popperInstance.current) {
        popperInstance.current.destroy()
        popperInstance.current = null
      }
    }
    const createPopover = (): void => {
      if (!popperInstance.current) {
        popperInstance.current = createPopper(triggerElement, popoverRef.current, POPPER_OPTIONS)
      }
    }

    active ? createPopover() : destroyPopover()
  }, [active, triggerElement])

  return (
    <div
      className={classnames(styles.popover, 'like-popover', {
        [styles.popoverActive]: active,
      })}
      ref={popoverRef}
    >
      <div className={classnames(styles.arrow, 'like-popover-arrow')} data-popper-arrow></div>

      {children}
    </div>
  )
}

export default Popover
